import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';

import {
  StickyHeaderTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'common/ui/table';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { cn } from 'utils/cn';
import { DataTableViewOptions } from './data-table-view-options';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[] | any;
  data: TData[];
  FilterForm?: React.ComponentType;
  tableName?: string;
  tableClassName?: string;
  containerClassName?: string;
  noViewOptions?: boolean;
  // Pads the body with blank rows up to this count so a paginated table
  // holds a stable height across pages (e.g. a 3-row last page doesn't
  // collapse the layout below it). Only applies once there's at least one
  // real row - an empty page still falls through to the "No results" row.
  minRows?: number;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  FilterForm,
  tableName,
  tableClassName,
  containerClassName,
  noViewOptions = false,
  minRows,
}: DataTableProps<TData, TValue>) {
  const location = useLocation();

  const subName = tableName ? tableName : location.pathname;

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const rows = table.getRowModel().rows;
  const fillerRowCount =
    minRows && rows.length > 0 ? Math.max(0, minRows - rows.length) : 0;

  return (
    <div>
      <div className="flex flex-wrap justify-between pb-1">
        {FilterForm && <FilterForm />}
        {noViewOptions ? null : (
          <DataTableViewOptions table={table} tableName={subName} />
        )}
      </div>

      <StickyHeaderTable
        className={tableClassName}
        containerClassName={containerClassName}
      >
        <TableHeader className="sticky top-0 z-20 bg-secondary">
          {table.getHeaderGroups().map(headerGroup => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header, index) => {
                return (
                  <TableHead
                    key={`${headerGroup.id}-${header.id}-${index}`}
                    style={header.column.columnDef.meta?.style || {}}
                    className={cn(
                      'border-b border-muted',
                      header.column.columnDef.meta?.className
                    )}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {rows.length ? (
            <>
              {rows.map((row, rowIndex) => (
                <TableRow
                  key={rowIndex}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell, cellIndex) => (
                    <TableCell
                      className={cn(
                        'border-b border-muted',
                        cell.column.columnDef.meta?.className
                      )}
                      key={`${rowIndex}-${cellIndex}`}
                      style={cell.column.columnDef.meta?.style || {}}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {Array.from({ length: fillerRowCount }).map((_, fillerIndex) => (
                <TableRow key={`filler-${fillerIndex}`} className="hover:bg-transparent">
                  <TableCell colSpan={columns.length} className="border-b border-muted">
                    &nbsp;
                  </TableCell>
                </TableRow>
              ))}
            </>
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                No results.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </StickyHeaderTable>
    </div>
  );
}
